import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useVersionSkew } from '@/hooks/useVersionSkewNotice';

/**
 * Sticky countdown banner shown on production stations when this tab is
 * running a stale build. Deliberately has NO dismiss control — dismissal is
 * exactly what left a station on a stale bundle for a whole shift (Momo /
 * Ningbo, 2026-08-26). See PRODUCTION_DESIGN.md §2.1 for the z-index ladder
 * this banner must respect (sticky-banner tier, z-20 — under all
 * operator-blocking modals).
 */
export function VersionUpdateBanner() {
  const { t } = useTranslation('production');
  const { skewDetected, safeToReload, secondsRemaining, manualOnly } = useVersionSkew({ mode: 'auto-reload' });
  const bannerRef = useRef<HTMLDivElement>(null);

  // Publish the banner's rendered height as `--version-banner-height` so a
  // full-viewport surface (the Client messages inbox) can subtract it. Resets
  // to 0px when hidden/unmounted. Same convention as PageActionBar.
  useEffect(() => {
    const el = bannerRef.current;
    if (!skewDetected || !el || typeof ResizeObserver === 'undefined') return;
    const root = document.documentElement;
    const publish = () => {
      root.style.setProperty('--version-banner-height', `${el.offsetHeight}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.setProperty('--version-banner-height', '0px');
    };
  }, [skewDetected]);

  if (!skewDetected) return null;

  const isFinalCountdown = !manualOnly && safeToReload && secondsRemaining !== null && secondsRemaining <= 5;
  const statusLine = manualOnly
    ? t('versionUpdate.manual')
    : safeToReload
      ? t('versionUpdate.countdown', { seconds: secondsRemaining ?? 0 })
      : t('versionUpdate.waiting');

  return (
    <div
      ref={bannerRef}
      className={cn(
        // z-30: above the stations' own sticky z-20 headers (PrintStationPage
        // scan bar is sticky top-0 z-20 later in the tree), still below every
        // operator-blocking modal (z-50) and PackingCompletionOverlay (z-60).
        'sticky top-0 z-30 flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between',
        isFinalCountdown ? 'bg-red-600 text-white' : 'bg-amber-100 text-amber-900'
      )}
    >
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-6 w-6 flex-shrink-0" aria-hidden="true" />
        <div>
          {/* Live region scoped to the title only — the countdown line changes
              every second and would re-announce 20 times if it were live. */}
          <p role="status" aria-live="polite" className="text-2xl font-semibold">
            {t('versionUpdate.title')}
          </p>
          <p className="text-lg">{statusLine}</p>
        </div>
      </div>
      <Button
        type="button"
        onClick={() => window.location.reload()}
        className={cn('h-14 min-h-12 px-8 text-lg', isFinalCountdown && 'bg-white text-red-700 hover:bg-white/90')}
      >
        {t('versionUpdate.refreshNow')}
      </Button>
    </div>
  );
}
